import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Package, 
  MapPin, 
  Bell, 
  User, 
  Settings, 
  LogOut,
  PlusCircle,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { qrService } from '../../services/qrService'
import toast from 'react-hot-toast'
import '../../styles/CustomerDashboard.css';

export default function CustomerDashboard() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [activeTab, setActiveTab] = useState('objects')
  const [objects, setObjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [newObject, setNewObject] = useState({
    qrId: '',
    objectName: '',
    description: '',
    category: 'personal',
    estimatedValue: '',
    photos: []
  })
  const [showAddForm, setShowAddForm] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/')
      return
    }
    loadObjects()
  }, [user])

  const loadObjects = async () => {
    setLoading(true)
    try {
      // Charger les objets de l'utilisateur depuis Firestore
      // Ceci est un exemple statique
      const mockObjects = [
        {
          id: '1',
          qrId: 'QR1001',
          objectName: 'Portefeuille',
          description: 'Portefeuille noir en cuir',
          status: 'active',
          lastSeen: new Date().toISOString(),
          createdAt: new Date(Date.now() - 86400000).toISOString(),
          category: 'personal'
        },
        {
          id: '2',
          qrId: 'QR1002',
          objectName: 'Clés de voiture',
          description: 'Clés avec porte-clés rouge',
          status: 'lost',
          lastSeen: new Date(Date.now() - 3600000).toISOString(),
          createdAt: new Date(Date.now() - 172800000).toISOString(),
          category: 'vehicle'
        },
        {
          id: '3',
          qrId: 'QR1003',
          objectName: 'Passeport',
          description: 'Passeport français',
          status: 'active',
          lastSeen: new Date().toISOString(),
          createdAt: new Date(Date.now() - 259200000).toISOString(),
          category: 'documents'
        }
      ]
      setObjects(mockObjects)
    } catch (error) {
      console.error('Error loading objects:', error)
      toast.error('Erreur de chargement des objets')
    } finally {
      setLoading(false)
    }
  }

  const handleAddObject = async (e) => {
    e.preventDefault()
    
    if (!newObject.qrId || !newObject.objectName) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    try {
      // Assigner le QR code à l'objet
      const objectData = {
        objectName: newObject.objectName,
        description: newObject.description,
        category: newObject.category,
        estimatedValue: newObject.estimatedValue,
        ownerPhoneNumbers: [user.phone], // À récupérer du profil
        createdAt: new Date().toISOString()
      }

      const assignedObject = await qrService.assignQRCode(
        newObject.qrId,
        user.uid,
        objectData
      )

      setObjects([assignedObject, ...objects])
      setNewObject({
        qrId: '',
        objectName: '',
        description: '',
        category: 'personal',
        estimatedValue: '',
        photos: []
      })
      setShowAddForm(false)
      
      toast.success('Objet ajouté avec succès !')
    } catch (error) {
      console.error('Error adding object:', error)
      toast.error(error.message || 'Erreur lors de l\'ajout')
    }
  }

  const handleMarkAsLost = async (objectId) => {
    try {
      // Mettre à jour le statut de l'objet dans Firestore
      // await qrService.markObjectAsLost(objectId)
      
      setObjects(objects.map(obj => 
        obj.id === objectId 
          ? { ...obj, status: 'lost', lastSeen: new Date().toISOString() }
          : obj
      ))
      
      toast.success('Objet signalé comme perdu')
    } catch (error) {
      console.error('Error marking as lost:', error)
      toast.error('Erreur lors du signalement')
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="text-center py-xl">
          <div className="spinner"></div>
          <p className="mt-md">Chargement de vos objets...</p>
        </div>
      )
    }

    switch (activeTab) {
      case 'objects':
        return (
          <div className="space-y-lg">
            {/* Bouton d'ajout */}
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Mes objets</h3>
              <button
                onClick={() => setShowAddForm(true)}
                className="btn btn-primary"
              >
                <PlusCircle size={20} />
                Ajouter un objet
              </button>
            </div>

            {/* Formulaire d'ajout */}
            {showAddForm && (
              <div className="card">
                <h4 className="font-semibold mb-md">Ajouter un nouvel objet</h4>
                <form onSubmit={handleAddObject}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-md mb-md">
                    <div className="form-group">
                      <label className="form-label">ID QR Code *</label>
                      <input
                        type="text"
                        value={newObject.qrId}
                        onChange={(e) => setNewObject({...newObject, qrId: e.target.value})}
                        className="form-control"
                        placeholder="Ex: QR1001"
                        required
                      />
                      <p className="form-text">
                        Scannez votre sticker QR code pour obtenir l'ID
                      </p>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nom de l'objet *</label>
                      <input
                        type="text"
                        value={newObject.objectName}
                        onChange={(e) => setNewObject({...newObject, objectName: e.target.value})}
                        className="form-control"
                        placeholder="Ex: Portefeuille, Clés..."
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Catégorie</label>
                      <select
                        value={newObject.category}
                        onChange={(e) => setNewObject({...newObject, category: e.target.value})}
                        className="form-control"
                      >
                        <option value="personal">Personnel</option>
                        <option value="documents">Documents</option>
                        <option value="electronics">Électronique</option>
                        <option value="vehicle">Véhicule</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Valeur estimée (FCFA)</label>
                      <input
                        type="number"
                        value={newObject.estimatedValue}
                        onChange={(e) => setNewObject({...newObject, estimatedValue: e.target.value})}
                        className="form-control"
                        placeholder="Ex: 50000"
                      />
                    </div>
                  </div>
                  <div className="form-group mb-md">
                    <label className="form-label">Description</label>
                    <textarea
                      value={newObject.description}
                      onChange={(e) => setNewObject({...newObject, description: e.target.value})}
                      className="form-control"
                      rows="3"
                      placeholder="Décrivez votre objet en détail..."
                    />
                  </div>
                  <div className="flex gap-sm">
                    <button type="submit" className="btn btn-primary">
                      Enregistrer
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="btn btn-outline"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Liste des objets */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg">
              {objects.map((object) => (
                <div key={object.id} className="card">
                  <div className="flex items-start justify-between mb-sm">
                    <h4 className="font-semibold">{object.objectName}</h4>
                    <span className={`badge ${
                      object.status === 'active' ? 'badge-success' :
                      object.status === 'lost' ? 'badge-error' :
                      'badge-info'
                    }`}>
                      {object.status === 'active' ? 'Actif' : 
                       object.status === 'lost' ? 'Perdu' : 'Retrouvé'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted mb-md">{object.description}</p>
                  
                  <div className="space-y-sm text-sm">
                    <div className="flex items-center gap-sm">
                      <Package size={16} className="text-muted" />
                      <span>QR: <code>{object.qrId}</code></span>
                    </div>
                    <div className="flex items-center gap-sm">
                      <Clock size={16} className="text-muted" />
                      <span>
                        Ajouté le {new Date(object.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {object.lastSeen && (
                      <div className="flex items-center gap-sm">
                        <MapPin size={16} className="text-muted" />
                        <span>
                          Dernière position: {new Date(object.lastSeen).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-md pt-md border-t">
                    {object.status === 'active' ? (
                      <button
                        onClick={() => handleMarkAsLost(object.id)}
                        className="btn btn-outline btn-sm w-full"
                      >
                        <AlertCircle size={16} />
                        Signaler comme perdu
                      </button>
                    ) : object.status === 'lost' ? (
                      <div className="text-center">
                        <AlertCircle size={24} className="mx-auto mb-sm" style={{ color: 'var(--color-warning)' }} />
                        <p className="text-sm text-muted">
                          Objet signalé comme perdu. Vous serez alerté dès qu'il sera retrouvé.
                        </p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <CheckCircle size={24} className="mx-auto mb-sm" style={{ color: 'var(--color-success)' }} />
                        <p className="text-sm text-muted">
                          Objet retrouvé ! Consultez les détails.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {objects.length === 0 && !showAddForm && (
              <div className="card text-center py-xl">
                <Package size={64} className="mx-auto mb-md text-muted" />
                <h4 className="mb-sm">Aucun objet enregistré</h4>
                <p className="text-muted mb-md">
                  Commencez par ajouter vos premiers objets à protéger
                </p>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="btn btn-primary"
                >
                  Ajouter mon premier objet
                </button>
              </div>
            )}
          </div>
        )

      case 'profile':
        return (
          <div className="card max-w-2xl">
            <h3 className="font-semibold mb-lg">Mon profil</h3>
            <div className="space-y-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                <div className="form-group">
                  <label className="form-label">Nom complet</label>
                  <input
                    type="text"
                    className="form-control"
                    defaultValue={user?.name || ''}
                    placeholder="Votre nom"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={user?.email || ''}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Téléphone principal *</label>
                  <input
                    type="tel"
                    className="form-control"
                    defaultValue={user?.phone || ''}
                    placeholder="+225 07 00 00 00 00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Téléphone secondaire</label>
                  <input
                    type="tel"
                    className="form-control"
                    defaultValue={user?.phone2 || ''}
                    placeholder="+225 01 00 00 00 00"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Adresse</label>
                <textarea
                  className="form-control"
                  rows="3"
                  defaultValue={user?.address || ''}
                  placeholder="Votre adresse complète"
                />
              </div>
              
              <div>
                <h4 className="font-semibold mb-md">Préférences de notification</h4>
                <div className="space-y-sm">
                  <label className="flex items-center gap-sm">
                    <input type="checkbox" defaultChecked />
                    <span>Notifications SMS</span>
                  </label>
                  <label className="flex items-center gap-sm">
                    <input type="checkbox" defaultChecked />
                    <span>Notifications Email</span>
                  </label>
                  <label className="flex items-center gap-sm">
                    <input type="checkbox" />
                    <span>Notifications WhatsApp</span>
                  </label>
                </div>
              </div>
              
              <button className="btn btn-primary">
                Enregistrer les modifications
              </button>
            </div>
          </div>
        )

      case 'notifications':
        return (
          <div className="card">
            <h3 className="font-semibold mb-lg">Notifications</h3>
            <div className="space-y-sm">
              {[
                { id: 1, type: 'info', message: 'Votre objet "Portefeuille" a été scanné', time: 'Il y a 2 heures', read: false },
                { id: 2, type: 'success', message: 'Objet "Clés de voiture" retrouvé !', time: 'Hier', read: true },
                { id: 3, type: 'warning', message: 'Pensez à vérifier vos objets', time: 'Il y a 3 jours', read: true },
              ].map((notification) => (
                <div
                  key={notification.id}
                  className={`p-md rounded border ${
                    !notification.read ? 'border-l-4' : ''
                  }`}
                  style={!notification.read ? { 
                    borderLeftColor: 'var(--color-primary)',
                    backgroundColor: 'var(--color-surface)' 
                  } : {}}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{notification.message}</p>
                      <p className="text-sm text-muted">{notification.time}</p>
                    </div>
                    {!notification.read && (
                      <span className="badge badge-primary">Nouveau</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
    }
  }

  return (
    <div className="page-container">
      {/* Header */}
      <header className="page-header">
        <div className="container flex items-center justify-between">
          <div className="flex items-center gap-md">
            <div className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
              UltimaTag
            </div>
            <nav className="hidden md:flex items-center gap-lg">
              <button
                onClick={() => setActiveTab('objects')}
                className={`text-sm ${
                  activeTab === 'objects' ? 'font-semibold' : 'text-muted'
                }`}
              >
                Mes objets
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`text-sm ${
                  activeTab === 'notifications' ? 'font-semibold' : 'text-muted'
                }`}
              >
                Notifications
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`text-sm ${
                  activeTab === 'profile' ? 'font-semibold' : 'text-muted'
                }`}
              >
                Mon profil
              </button>
            </nav>
          </div>
          <div className="flex items-center gap-sm">
            <button
              onClick={() => navigate('/')}
              className="btn btn-outline btn-sm"
            >
              Accueil
            </button>
            <button
              onClick={handleLogout}
              className="btn btn-outline btn-sm"
              style={{ color: 'var(--color-error)' }}
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <div className="md:hidden border-b">
        <div className="container flex">
          <button
            onClick={() => setActiveTab('objects')}
            className={`flex-1 text-center p-sm ${
              activeTab === 'objects' ? 'font-semibold border-b-2' : 'text-muted'
            }`}
            style={activeTab === 'objects' ? { borderBottomColor: 'var(--color-primary)' } : {}}
          >
            <Package size={20} className="mx-auto mb-xs" />
            <span className="text-xs">Objets</span>
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`flex-1 text-center p-sm ${
              activeTab === 'notifications' ? 'font-semibold border-b-2' : 'text-muted'
            }`}
            style={activeTab === 'notifications' ? { borderBottomColor: 'var(--color-primary)' } : {}}
          >
            <Bell size={20} className="mx-auto mb-xs" />
            <span className="text-xs">Alertes</span>
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 text-center p-sm ${
              activeTab === 'profile' ? 'font-semibold border-b-2' : 'text-muted'
            }`}
            style={activeTab === 'profile' ? { borderBottomColor: 'var(--color-primary)' } : {}}
          >
            <User size={20} className="mx-auto mb-xs" />
            <span className="text-xs">Profil</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="page-main">
        <div className="container">
          <div className="mb-lg">
            <h1 className="text-2xl font-bold">Mon tableau de bord</h1>
            <p className="text-muted">
              Gérez vos objets protégés et consultez vos notifications
            </p>
          </div>
          
          {renderTabContent()}
        </div>
      </main>
    </div>
  )
}